import type { ReactNode } from "react"
import { createRoot } from "react-dom/client"

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { AdjudicateTab } from "@/containers/AdjudicateTab"
import { PagedTable, type TabDef } from "@/containers/PagedTable"
import { SearchTab } from "@/containers/SearchTab"
import { client } from "@/lib/client"
import { courtFilters, judgeFilters, lawFilters } from "@/lib/filters"
import { dash } from "@/lib/utils"
import { useNav, type Nav } from "@/lib/hooks"
import type { LawDocType } from "@shared/types"
import { CaseDetail } from "@/pages/CaseDetail"
import { CourtDetail } from "@/pages/CourtDetail"
import { JudgeDetail } from "@/pages/JudgeDetail"
import { LawDetail } from "@/pages/LawDetail"
import "./styles.css"

// Checks each tab's cols/keyOf against the row type inferred from its fetchPage,
// then erases the row type so TABS can hold all three tabs in one array.
const defineTab = <Row,>(tab: TabDef<Row>): TabDef<any> => tab

const lawTab = (id: string, label: string, path: string, type: LawDocType, withYear = true) =>
  defineTab({
    id,
    label,
    path,
    detailBase: "/laws",
    fetchPage: (query) => client.api.laws.$get({ query: { ...query, type } }),
    keyOf: (r) => r.actId,
    filters: lawFilters,
    cols: [
      {
        header: "Citation",
        cell: (r) => (
          <span className="block max-w-[14rem] truncate" title={r.citation ?? undefined}>
            {dash(r.citation)}
          </span>
        ),
      },
      {
        header: "Title",
        cell: (r) => (
          <span className="block max-w-[16rem] truncate" title={r.titleName ?? undefined}>
            {dash(r.titleName)}
          </span>
        ),
      },
      {
        header: "Section",
        cell: (r) => (
          <span className="block max-w-[16rem] truncate" title={r.sectionTitle ?? undefined}>
            {dash(r.sectionTitle)}
          </span>
        ),
      },
      { header: "Jurisdiction", cell: (r) => dash(r.state) },
      ...(withYear ? [{ header: "Year", cell: (r: { year: number | null }) => dash(r.year) }] : []),
    ],
  })

const STATUTE_TABS = [
  lawTab("statutes", "Statutes", "/statutes", "statute"),
  lawTab("statutes-compiled", "Statutes (Compiled)", "/statutes/compiled", "statute_compilation"),
  lawTab("session-laws", "Session Laws", "/statutes/session-laws", "session_law"),
]

const TABS = [
  defineTab({
    id: "cases",
    label: "Cases",
    path: "/cases",
    detailBase: "/cases",
    fetchPage: (query) => client.api.cases.$get({ query }),
    keyOf: (r) => r.id,
    cols: [
      {
        header: "Case",
        cell: (r) => (
          <span className="block max-w-[22rem] truncate" title={r.caseName ?? undefined}>
            {dash(r.caseName)}
          </span>
        ),
      },
      {
        header: "Court",
        cell: (r) => (
          <span className="block max-w-[16rem] truncate" title={r.courtName ?? undefined}>
            {dash(r.courtName)}
          </span>
        ),
      },
      { header: "Filed", cell: (r) => dash(r.dateFiled) },
      { header: "Judges", cell: (r) => dash(r.judges) },
      { header: "Citations", cell: (r) => r.citationCount },
    ],
  }),
  lawTab("constitution", "Constitution", "/constitution", "constitution", false),
  STATUTE_TABS[0]!,
  lawTab("regulations", "Regulations", "/regulations", "regulation"),
  lawTab("court-rules", "Court Rules", "/court-rules", "court_rule"),
  defineTab({
    id: "courts",
    label: "Courts",
    path: "/courts",
    detailBase: "/courts",
    fetchPage: (query) => client.api.courts.$get({ query }),
    keyOf: (r) => r.id,
    filters: courtFilters,
    cols: [
      { header: "Name", cell: (r) => dash(r.fullName) },
      { header: "Jurisdiction", cell: (r) => dash(r.jurisdiction) },
      { header: "Citation", cell: (r) => dash(r.citationString) },
    ],
  }),
  defineTab({
    id: "judges",
    label: "Judges",
    path: "/judges",
    detailBase: "/judges",
    fetchPage: (query) => client.api.judges.$get({ query }),
    keyOf: (r) => r.id,
    filters: judgeFilters,
    cols: [
      { header: "Name", cell: (r) => dash(r.name) },
      { header: "Born", cell: (r) => dash(r.dateDob) },
      { header: "State", cell: (r) => dash(r.dobState) },
    ],
  }),
]

function StatutesTab({ params, nav }: { params: URLSearchParams; nav: Nav }) {
  const active = STATUTE_TABS.find((t) => t.path === location.pathname) ?? STATUTE_TABS[0]!
  return (
    <Tabs value={active.id} onValueChange={(id) => nav(STATUTE_TABS.find((t) => t.id === id)!.path)}>
      <TabsList variant="text" className="-mt-2 mb-2 touch-pan-x justify-start overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {STATUTE_TABS.map((t) => (
          <TabsTrigger key={t.id} value={t.id}>
            {t.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {STATUTE_TABS.map((t) => (
        <TabsContent key={t.id} value={t.id}>
          {t.id === active.id && <PagedTable tab={t} params={params} nav={nav} />}
        </TabsContent>
      ))}
    </Tabs>
  )
}

const ADJUDICATE_PATH = "/"
const SEARCH_PATH = "/search"

const DETAILS: Record<string, (id: string, nav: Nav) => ReactNode> = {
  cases: (id, nav) => <CaseDetail id={id} nav={nav} />,
  courts: (id, nav) => <CourtDetail id={id} nav={nav} />,
  judges: (id, nav) => <JudgeDetail id={id} nav={nav} />,
  laws: (id, nav) => <LawDetail id={id} nav={nav} />,
}

function App() {
  const nav = useNav()
  const parts = location.pathname.split("/").filter(Boolean)

  if (parts.length === 2 && parts[0]! in DETAILS) {
    return <>{DETAILS[parts[0]!]!(decodeURIComponent(parts[1]!), nav)}</>
  }

  const activeId =
    TABS.find((t) => t.path === location.pathname)?.id ??
    (location.pathname === SEARCH_PATH
      ? "search"
      : location.pathname.startsWith("/statutes")
        ? "statutes"
        : "adjudicate")
  const params = new URLSearchParams(location.search)

  const onTab = (id: string) =>
    nav(
      id === "adjudicate"
        ? ADJUDICATE_PATH
        : id === "search"
          ? SEARCH_PATH
          : TABS.find((t) => t.id === id)!.path,
    )

  return (
    <div className="mx-auto my-6 max-w-4xl border-l border-dotted py-2 pr-4 pl-7">
      <Tabs value={activeId} onValueChange={onTab}>
        <div className="mb-2 flex items-center gap-6">
          <h1 className="shrink-0 text-base font-medium">
            <a href="/" className="text-inherit font-bold">
              lyberry
            </a>
          </h1>
          <TabsList
            variant="text"
            className="touch-pan-x justify-start overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <TabsTrigger value="adjudicate">Adjudicate</TabsTrigger>
            <TabsTrigger value="search">Search</TabsTrigger>
            {TABS.map((t) => (
              <TabsTrigger key={t.id} value={t.id}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        <TabsContent value="adjudicate">
          {activeId === "adjudicate" && <AdjudicateTab />}
        </TabsContent>
        <TabsContent value="search">{activeId === "search" && <SearchTab />}</TabsContent>
        {TABS.map((t) => (
          <TabsContent key={t.id} value={t.id}>
            {t.id === activeId &&
              (t.id === "statutes" ? (
                <StatutesTab params={params} nav={nav} />
              ) : (
                <PagedTable tab={t} params={params} nav={nav} />
              ))}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}

createRoot(document.getElementById("root")!).render(<App />)
